import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import CustomButton from "../../components/CommonButton/CommonButton";
import { Box, Alert, CircularProgress, Typography, Paper } from "@mui/material";
import {
  GET_JOIN_QRCODE_PATH,
  GENERATE_JOIN_QRCODE_PATH
} from "../../utils/url";
import Loader from "../../components/loader/Loader";
import { downloadFileFromS3 } from "../../utils/commonFunctions";

interface QRCodeData {
  qrCodeImageUrl?: string;
  qrCodeText?: string;
  hasActiveQRCode?: boolean;
}

interface JoinRewardData {
  rewardType?: number; // 1: Item, 2: Points, 3: Item & Points
  shortDescription?: string;
  points?: number;
  expirationInDays?: number;
  maxRedemptionLimit?: number;
  isActive?: boolean;
}

interface JoinQRCodeProps {
  onSwitchToRewardsTab?: () => void;
}

const JoinQRCode: React.FC<JoinQRCodeProps> = ({ onSwitchToRewardsTab }) => {
  const navigate = useNavigate();
  const { businessId } = useParams<any>();

  const [qrCodeData, setQrCodeData] = useState<QRCodeData | null>(null);
  const [rewardData, setRewardData] = useState<JoinRewardData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: "success" as "success" | "error" | "info" | "warning"
  });

  const rewardTypeOptions = [
    { value: 1, label: "Item" },
    { value: 2, label: "Points" },
    { value: 3, label: "Item & Points" }
  ];

  const expirationOptions = [
    { value: 7, label: "1 Week" },
    { value: 30, label: "1 Month" }
  ];

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      "Content-Type": "application/json"
    }),
    [localStorage.getItem("accessToken")]
  );

  const fetchQRCodeData = useCallback(async () => {
    if (!businessId) return;
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}${GET_JOIN_QRCODE_PATH}/${businessId}`,
        { headers }
      );

      // Handle response based on actual API response structure
      if (response.data) {
        const data = response.data;
        setQrCodeData({
          qrCodeImageUrl: data.QRCodeImageUrl,
          qrCodeText: data.QRCode,
          hasActiveQRCode: !!(data.QRCodeImageUrl && data.IsActive)
        });

        // Set reward data if exists in response
        if (
          data.rewardData ||
          data.reward ||
          data.joinReward ||
          data.JoinReward
        ) {
          const reward =
            data.rewardData ||
            data.reward ||
            data.joinReward ||
            data.JoinReward;
          setRewardData({
            rewardType: reward.rewardType || reward.RewardType,
            shortDescription:
              reward.shortDescription || reward.ShortDescription,
            points: reward.points || reward.Points,
            expirationInDays:
              reward.expirationInDays || reward.ExpirationInDays,
            maxRedemptionLimit:
              reward.maxRedemptionLimit || reward.MaxRedemptionLimit,
            isActive:
              reward.isActive !== undefined
                ? reward.isActive
                : reward.IsActive === 1 || reward.IsActive === true
          });
        } else {
          setRewardData(null);
        }
      }
    } catch (error: any) {
      console.error("Error fetching QR code:", error);
      // If 404 or no QR code exists, set empty state
      if (error.response?.status === 404) {
        setQrCodeData(null);
        setRewardData(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [businessId, headers]);

  useEffect(() => {
    fetchQRCodeData();
  }, [fetchQRCodeData]);

  const handleGenerateQRCode = async () => {
    if (!businessId) return;
    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${GENERATE_JOIN_QRCODE_PATH}/${businessId}`,
        {},
        { headers }
      );

      // Handle response based on actual API response structure
      if (response.data) {
        setNotificationMessage({
          message: "QR code generated successfully",
          type: "success"
        });
        // Fetch the updated QR code data including reward information
        await fetchQRCodeData();
      }
    } catch (error) {
      console.error("Error generating QR code:", error);
      setNotificationMessage({
        message: "Failed to generate QR code. Please try again.",
        type: "error"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadImage = () => {
    if (qrCodeData?.qrCodeImageUrl) {
      downloadFileFromS3(qrCodeData.qrCodeImageUrl);
    }
  };

  const handleConfigureReward = () => {
    if (onSwitchToRewardsTab) {
      onSwitchToRewardsTab();
    } else {
      // Fallback to navigation if callback is not provided
      if (businessId) {
        navigate(`/business-details/${businessId}?tab=5`);
      }
    }
  };

  return isLoading ? (
    <Loader />
  ) : (
    <Box>
      {/* Notification Message */}
      {notificationMessage.message && (
        <Alert
          severity={notificationMessage.type}
          sx={{ mb: 2 }}
          onClose={() =>
            setNotificationMessage({ message: "", type: "success" })
          }
        >
          {notificationMessage.message}
        </Alert>
      )}

      {/* Generate QR Code Section */}
      {!qrCodeData?.hasActiveQRCode && (
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <CustomButton
            variant="contained"
            className="blue_button"
            onClick={handleGenerateQRCode}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Generate Join QR Code"
            )}
          </CustomButton>
        </Box>
      )}

      {/* QR Code Display Section */}
      {qrCodeData?.hasActiveQRCode && (
        <>
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 600, color: "#192F67" }}
          >
            Join QR Code
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start"
            }}
          >
            {qrCodeData.qrCodeImageUrl && (
              <Box
                component="img"
                src={qrCodeData.qrCodeImageUrl}
                alt="Join QR Code"
                sx={{
                  width: 200,
                  height: 200,
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "block"
                }}
              />
            )}
            {qrCodeData.qrCodeText && (
              <Typography
                variant="body1"
                sx={{
                  color: "#1976d2",
                  mb: 2,
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" }
                }}
                onClick={() => {
                  navigator.clipboard.writeText(qrCodeData.qrCodeText || "");
                }}
              >
                {qrCodeData.qrCodeText}
              </Typography>
            )}
            <CustomButton
              variant="contained"
              className="blue_button"
              onClick={handleDownloadImage}
              sx={{ minWidth: 150 }}
            >
              Download Image
            </CustomButton>
          </Box>

          {/* Configure Reward Section */}
          {(!rewardData || !rewardData.isActive) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
                gap: 2
              }}
            >
              <Typography variant="body1" sx={{ mt: 2 }}>
                Do you want to give join reward to the user?
              </Typography>

              <Box>
                <CustomButton
                  variant="contained"
                  className="blue_button"
                  onClick={handleConfigureReward}
                >
                  Configure Reward
                </CustomButton>
              </Box>
            </Box>
          )}

          {/* Reward Details Section */}
          {rewardData && rewardData.isActive && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, color: "#192F67" }}
              >
                Join Reward Detail
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Reward Type:
                  </Typography>
                  <Typography variant="body1">
                    {rewardTypeOptions.find(
                      (opt) => opt.value === rewardData.rewardType
                    )?.label || "-"}
                  </Typography>
                </Box>
                {(rewardData.rewardType === 1 || rewardData.rewardType === 3) &&
                  rewardData.shortDescription && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Item:
                      </Typography>
                      <Typography variant="body1">
                        {rewardData.shortDescription}
                      </Typography>
                    </Box>
                  )}
                {(rewardData.rewardType === 2 || rewardData.rewardType === 3) &&
                  rewardData.points !== undefined && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Points:
                      </Typography>
                      <Typography variant="body1">
                        {rewardData.points}
                      </Typography>
                    </Box>
                  )}
                {(rewardData.rewardType === 1 || rewardData.rewardType === 3) &&
                  rewardData.expirationInDays && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Expiration:
                      </Typography>
                      <Typography variant="body1">
                        {expirationOptions.find(
                          (opt) => opt.value === rewardData.expirationInDays
                        )?.label || `${rewardData.expirationInDays} days`}
                      </Typography>
                    </Box>
                  )}
                {rewardData.maxRedemptionLimit !== undefined && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Max Redemption Limit:
                    </Typography>
                    <Typography variant="body1">
                      {rewardData.maxRedemptionLimit}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Typography variant="body1">
                    {rewardData.isActive ? "Active" : "Inactive"}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <CustomButton
                    variant="outlined"
                    className="blue_button"
                    onClick={handleConfigureReward}
                  >
                    Edit Reward
                  </CustomButton>
                </Box>
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default JoinQRCode;
